import { TableHead, Th, Tr, Td } from '../../Elements/DataTable';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  HiOutlineArchive,
  HiOutlinePencil,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineX,
} from "react-icons/hi";

import ButtonBar from "../../Elements/ButtonBar";
import Button from '../../Elements/Button';
import { UserContext } from "../../../context/UserContext";
import apiFetch from "../../../context/api";
import useCambioCaja from "../../../lib/hooks/useCambioCaja";

const COIN_DENOMINATIONS = [2, 1, 0.5, 0.2, 0.1, 0.05, 0.02, 0.01];
const NOTE_DENOMINATIONS = [5, 10, 20, 50, 100, 200, 500];
const ALL_DENOMINATIONS = [...COIN_DENOMINATIONS, ...NOTE_DENOMINATIONS];

const storageKey = (venueId) => `leafgate_cash_boxes_${venueId || "global"}`;

function createEmptyCounts() {
  return Object.fromEntries(ALL_DENOMINATIONS.map((value) => [String(value), 0]));
}

function defaultBoxForm() {
  return { name: "", zone: "Todas", collaborators: [] };
}

// El objetivo orientativo de cambio a dejar al cerrar (la retirada = total - cambio) lo
// fija cada club en Configuración: se lee con useCambioCaja, no hay constante aquí.

function defaultSessionForm() {
  return {
    openingBalance: "",
    expectedBalance: "",
    notes: "",
    counts: createEmptyCounts(),
    changeAmount: "",
    // ¿La apertura se ha escrito a mano teniendo desglose delante? Entonces manda lo
    // escrito y el desglose se descarta: son dos formas de decir el mismo número y no
    // pueden convivir discrepándose (ficha saldo-pagos-caja #que-es-la-apertura).
    aperturaAMano: false,
  };
}

function sessionDraftKey(boxId, mode) {
  return `${mode}:${String(boxId)}`;
}

function formatMoney(value) {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function calculateCountsTotal(counts) {
  return ALL_DENOMINATIONS.reduce((sum, denomination) => {
    const qty = Number(counts?.[String(denomination)] || 0);
    return sum + denomination * qty;
  }, 0);
}

// ¿Hay desglose de monedas y billetes? Es lo que decide si la apertura sale del
// recuento o del importe declarado, aquí y en el backend.
// Un recuento a cero cuenta como SIN desglose, y a propósito: solo viajan las
// denominaciones con cantidad > 0 (`mapCountsToApiEntries`), así que un cajón contado
// y vacío llega al servidor igual que uno sin contar. Los dos lados caen entonces en el
// importe declarado, que el formulario vuelve a dejar editable y a la vista: si el
// cajón está de verdad vacío hay que escribir el 0 ahí. Deducir «recuento a 0 ⇒
// apertura 0» sería inventarse una apertura para toda caja que aún no se ha contado.
function hayDesglose(counts) {
  return ALL_DENOMINATIONS.some((denomination) => Number(counts?.[String(denomination)] || 0) > 0);
}

function normalizeCounts(raw) {
  const next = createEmptyCounts();
  ALL_DENOMINATIONS.forEach((denomination) => {
    next[String(denomination)] = Number(raw?.[String(denomination)] || 0);
  });
  return next;
}

function getStoredBoxes(venueId) {
  try {
    const raw = localStorage.getItem(storageKey(venueId));
    const parsed = JSON.parse(raw || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistBoxes(venueId, boxes) {
  localStorage.setItem(storageKey(venueId), JSON.stringify(boxes));
}

function getCollaboratorName(member) {
  if (!member) return "";
  if (member.profile?.first_name || member.profile?.last_name) {
    return `${member.profile?.first_name || ""} ${member.profile?.last_name || ""}`.trim();
  }
  return member.email || member.name || "";
}

function sortHistoryNewestFirst(history) {
  return [...history].sort((a, b) => {
    const left = new Date(b.closedAt || b.openedAt || 0).getTime();
    const right = new Date(a.closedAt || a.openedAt || 0).getTime();
    return left - right;
  });
}

// "¿Con cuánto se abre?" — UN solo sitio decide el importe de apertura, y lo
// usan por igual la tarjeta de caja abierta y el histórico: `opening_balance`, que es
// el que el cierre resta para sacar la diferencia.
//
// Hasta el 23-09-2026 esto devolvía el RECUENTO cuando lo había, con la idea de que la
// discrepancia con lo declarado "es justo lo que hay que ver". El efecto era el
// contrario: de las cuatro métricas de la tarjeta, «Saldo apertura» y «Recuento
// apertura» salían iguales siempre que hubiera desglose, el `opening_balance` —el que
// decide el arqueo— no se pintaba en ningún sitio, y el descuadre no se veía hasta el
// cierre, disfrazado de diferencia de caja. Es el reporte de CRUO: la pantalla decía
// 200 € y la aritmética usaba 165.
//
// Ahora los dos números no pueden separarse —al abrir y al editar, con desglose la
// apertura ES el recuento (backend `open_cash_box` / `update_open_cash_box_session`)—,
// así que solo divergen en turnos abiertos ANTES de ese arreglo. Para esos, el
// descuadre se enseña como lo que es: `descuadreApertura`.
function openingAmount(session) {
  return Number(session?.openingBalance || 0);
}

// Turnos abiertos antes del 23-09-2026: lo contado no coincide con la apertura que
// usará el cierre. Devuelve 0 cuando no hay nada que avisar.
// Ojo: mirar `hasOpeningCounts` y no `openingCountedTotal > 0` — un cajón contado y
// vacío se guarda con total 0, y con el total como criterio se anunciaría un descuadre
// falso en toda caja abierta sin desglose.
function descuadreApertura(session) {
  if (!session?.hasOpeningCounts) return 0;
  return Number(
    (Number(session.openingCountedTotal || 0) - Number(session.openingBalance || 0)).toFixed(2)
  );
}

function mapApiCountsToObject(entries) {
  const next = createEmptyCounts();
  if (!Array.isArray(entries)) return next;
  entries.forEach((entry) => {
    const key = String(entry?.denomination ?? "");
    if (!Object.prototype.hasOwnProperty.call(next, key)) return;
    next[key] = Number(entry?.quantity || 0);
  });
  return next;
}

function mapCountsToApiEntries(counts) {
  return ALL_DENOMINATIONS.map((denomination) => {
    const key = String(denomination);
    const quantity = Math.max(0, Number(counts?.[key] || 0));
    return {
      denomination: key,
      quantity,
      total: Number((denomination * quantity).toFixed(2)),
    };
  }).filter((entry) => entry.quantity > 0);
}

function mapApiSession(session) {
  if (!session) return null;
  return {
    id: session.id,
    status: session.closed_at ? "closed" : "open",
    openedAt: session.opened_at,
    closedAt: session.closed_at,
    openingBalance: Number(session.opening_balance || 0),
    openingCounts: mapApiCountsToObject(session.opening_counts),
    hasOpeningCounts: Array.isArray(session.opening_counts) && session.opening_counts.length > 0,
    openingCountedTotal: Number(session.opening_total || 0),
    expectedBalance: Number(session.expected_balance || 0),
    closingCounts: mapApiCountsToObject(session.closing_counts),
    closingCountedTotal: Number(session.closing_total || 0),
    difference: Number(session.difference || 0),
    changeTotal: Number(session.change_total || 0),
    withdrawal: Number(session.withdrawal || 0),
    notes: session.notes || "",
    closingNotes: session.notes || "",
  };
}

function mapApiBox(box, collaboratorMap) {
  const collaboratorIds = Array.isArray(box.collaborator_user_ids)
    ? box.collaborator_user_ids.map(String)
    : [];
  return {
    id: box.id,
    name: box.name,
    zone: box.zone || "Todas",
    collaboratorIds,
    collaborators: collaboratorIds.map((id) => collaboratorMap.get(id) || id),
    createdAt: box.created_at,
    updatedAt: box.updated_at,
    defaultOpeningBalance: Number(box.default_opening_balance || 0),
    currentSession: mapApiSession(box.current_session),
    sessions: Array.isArray(box.recent_sessions) ? box.recent_sessions.map(mapApiSession) : [],
  };
}

function DenominationFieldset({ title, denominations, counts, onChange }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 text-sm font-semibold text-gray-800">{title}</div>
      <div className="space-y-2">
        {denominations.map((denomination) => {
          const key = String(denomination);
          const qty = Number(counts?.[key] || 0);
          return (
            <div key={key} className="grid grid-cols-[84px_minmax(0,1fr)_100px] items-center gap-2">
              <div className="rounded-lg bg-gray-100 px-3 py-2 text-right font-semibold text-gray-700">
                {denomination >= 5 ? `${denomination}€` : `${denomination.toFixed(2)}€`}
              </div>
              <input
                type="number"
                min="0"
                step="1"
                value={qty}
                data-testid={`recuento-${key}`}
                onChange={(event) => onChange(key, event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
              />
              <div className="rounded-lg bg-emerald-50 px-3 py-2 text-right text-sm font-medium text-emerald-800">
                {formatMoney(denomination * qty)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ModalShell({ title, children, onClose }) {
  // Bloquear el scroll de la página mientras el modal está abierto: en tablet,
  // el dedo arrastraba el fondo en vez del contenido del modal («difícil de
  // desplazar», reporte CRUO 05-09-2026).
  useEffect(() => {
    const previo = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previo; };
  }, []);

  return (
    <div className="fixed inset-0 z-[120] flex items-start justify-center overflow-y-auto overscroll-contain bg-black/45 p-4 md:items-center">
      {/* La TARJETA limita su altura total con dvh (los vh de iPad mienten con la
          barra del navegador): antes el interior iba a 85vh y cabecera + padding
          la sacaban del viewport — «cortado». flex-col: la cabecera fija y el
          interior es la única zona de scroll. */}
      <div className="flex max-h-[calc(100dvh-2rem)] w-full max-w-5xl flex-col rounded-2xl border border-gray-200 bg-[#f7fbf2] shadow-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4">
          <h3 className="text-2xl font-semibold text-gray-800">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-gray-300 p-2 text-gray-600 hover:bg-gray-100"
          >
            <HiOutlineX size={20} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-5">{children}</div>
      </div>
    </div>
  );
}

export default function CajasPage() {
  const userContext = useContext(UserContext);
  const idealChange = useCambioCaja();
  const token = userContext[0];
  const venueID = userContext[3];
  const venueName = userContext[4];
  const accessPrivileges = userContext[7];
  const [boxes, setBoxes] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [modalState, setModalState] = useState({ type: null, boxId: null, sessionId: null });
  const [boxForm, setBoxForm] = useState(defaultBoxForm());
  const [sessionForm, setSessionForm] = useState(defaultSessionForm());

  // El cambio del club llega por un GET asíncrono y el modal de cierre se precarga UNA
  // vez al abrirlo. Si el valor llega con el modal ya abierto, y el campo sigue siendo el
  // que se precargó (nadie lo ha tocado), se actualiza: si no, la retirada se calcularía
  // sobre el 135 del arranque aunque el club tenga otro. Un campo que el encargado ya
  // editó no se pisa.
  const idealPrevioRef = useRef(idealChange);
  useEffect(() => {
    const previo = idealPrevioRef.current;
    idealPrevioRef.current = idealChange;
    if (previo === idealChange) return;
    setSessionForm((prev) =>
      modalState.type === "close-session" && prev.changeAmount === String(previo)
        ? { ...prev, changeAmount: String(idealChange) }
        : prev
    );
  }, [idealChange, modalState.type]);
  const [sessionDrafts, setSessionDrafts] = useState({});
  const fetchRequestRef = useRef(0);

  const apiHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  useEffect(() => {
    let cancelled = false;

    async function fetchCollaborators() {
      if (!token || !venueID) {
        setCollaborators([]);
        return;
      }

      try {
        const [staffResponse, ownersResponse] = await Promise.allSettled([
          apiFetch(`/v1/staff?venue_id=${encodeURIComponent(venueID)}`, { headers: apiHeaders }),
          apiFetch(`/v1/owners?venue_id=${encodeURIComponent(venueID)}`, { headers: apiHeaders }),
        ]);

        const collected = [];
        for (const result of [staffResponse, ownersResponse]) {
          if (result.status !== "fulfilled") continue;
          const response = result.value;
          if (!response.ok) continue;
          const payload = await response.json().catch(() => []);
          const list = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.users)
              ? payload.users
              : Array.isArray(payload?.staff)
                ? payload.staff
                : Array.isArray(payload?.owners)
                  ? payload.owners
                  : [];
          list.forEach((member) => {
            const name = getCollaboratorName(member);
            if (!name || !member?.id) return;
            collected.push({
              id: String(member.id),
              name,
            });
          });
        }

        const deduped = Array.from(
          new Map(collected.map((item) => [String(item.id), item])).values()
        );
        if (!cancelled) setCollaborators(deduped);
      } catch {
        if (!cancelled) setCollaborators([]);
      }
    }

    fetchCollaborators();
    return () => {
      cancelled = true;
    };
  }, [apiHeaders, token, venueID]);

  const collaboratorMap = useMemo(
    () => new Map(collaborators.map((item) => [String(item.id), item.name])),
    [collaborators]
  );
  // Cierres ya registrados: solo owner/superadmin (auditoría).
  const canEditCajaMoney = accessPrivileges === "owner" || accessPrivileges === "superadmin";
  // Caja ABIERTA (monedero de apertura): cualquiera que gestione la caja (incl. staff)
  // puede corregir su propio recuento si se equivoca.
  const canEditOpenCaja =
    canEditCajaMoney || accessPrivileges === "staff";

  const refreshBoxes = useCallback(async () => {
    if (!token || !venueID) {
      setBoxes([]);
      return;
    }

    const requestId = fetchRequestRef.current + 1;
    fetchRequestRef.current = requestId;
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await apiFetch(`/v1/cajas?venue_id=${encodeURIComponent(venueID)}`, {
        headers: apiHeaders,
      });
      if (!response.ok) {
        throw new Error("No se pudo cargar la gestión de cajas.");
      }
      const payload = await response.json();
      const items = Array.isArray(payload?.items) ? payload.items : [];
      const mapped = items.map((box) => mapApiBox(box, collaboratorMap));
      if (fetchRequestRef.current === requestId) {
        setBoxes(mapped);
        persistBoxes(venueID, mapped);
      }
    } catch {
      const fallback = getStoredBoxes(venueID);
      if (fetchRequestRef.current === requestId) {
        setBoxes(fallback);
        setErrorMessage(
          fallback.length
            ? "No se pudo refrescar la API de cajas. Se muestra la última copia local."
            : "No se pudo cargar la gestión de cajas."
        );
      }
    } finally {
      if (fetchRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [apiHeaders, collaboratorMap, token, venueID]);

  useEffect(() => {
    refreshBoxes();
  }, [refreshBoxes]);

  // El efectivo del local exige que haya UNA sola caja abierta: el backend lo verifica
  // en cada cobro y, desde el 23-09-2026, también al abrir. Aquí se apaga el botón para
  // que nadie llegue a ese 409 — antes se podía abrir la segunda sin queja y a partir de
  // ahí fallaba TODA aportación en efectivo del club.
  const cajaAbiertaDelLocal = useMemo(
    () => boxes.find((box) => box.currentSession?.status === "open") || null,
    [boxes]
  );

  const activeBox = useMemo(
    () => boxes.find((box) => box.id === modalState.boxId) || null,
    [boxes, modalState.boxId]
  );

  const history = useMemo(() => {
    const sessions = boxes.flatMap((box) =>
      (box.sessions || []).map((session) => ({
        ...session,
        boxId: box.id,
        boxName: box.name,
        zone: box.zone,
        collaborators: box.collaborators || [],
      }))
    );
    return sortHistoryNewestFirst(sessions);
  }, [boxes]);

  // Filas del Historial = sesiones ABIERTAS (caja abierta) + cierres registrados.
  const historyRows = useMemo(() => {
    const openRows = boxes
      .filter((box) => box.currentSession?.status === "open")
      .map((box) => ({
        ...box.currentSession,
        boxId: box.id,
        boxName: box.name,
        zone: box.zone,
        collaborators: box.collaborators || [],
        isOpen: true,
      }));
    return sortHistoryNewestFirst([...openRows, ...history]);
  }, [boxes, history]);

  const activeHistorySession = useMemo(() => {
    if (!modalState.sessionId) return null;
    return history.find((item) => item.id === modalState.sessionId) || null;
  }, [history, modalState.sessionId]);

  // Qué apertura se va a guardar, y cuánto mueve eso el esperado. Se calcula aquí y no
  // dentro del JSX para que el aviso del modal y el cuerpo del PATCH salgan del MISMO
  // número: un formulario que enseña una cifra y manda otra es justo el bug que
  // reportó CRUO, en pequeño.
  const totalDelRecuento = calculateCountsTotal(sessionForm.counts);
  const aperturaLaMandaElRecuento = hayDesglose(sessionForm.counts) && !sessionForm.aperturaAMano;
  // Escribir la apertura a mano teniendo desglose delante es renunciar a él. Se avisa y
  // el recuento viaja vacío, que es como el servidor entiende «sin desglose».
  // Solo si la cifra escrita DISCREPA de lo contado: reescribir 165 sobre un recuento de
  // 165 no es renunciar a nada, y borraba el desglose sin motivo — con él, el recuento
  // que el modal de cierre precarga después.
  const elRecuentoSeVaADescartar =
    hayDesglose(sessionForm.counts) &&
    sessionForm.aperturaAMano &&
    Number(sessionForm.openingBalance || 0) !== totalDelRecuento;
  const aperturaQueSeGuardara = aperturaLaMandaElRecuento
    ? totalDelRecuento
    : Number(sessionForm.openingBalance || 0);
  // Lo que viaja en el cuerpo sale del MISMO predicado que pinta el aviso: si el modal
  // dice que el desglose no se guarda, el desglose no se guarda.
  const recuentoQueSeGuardara = elRecuentoSeVaADescartar
    ? []
    : mapCountsToApiEntries(sessionForm.counts);
  const aperturaGuardada = Number(activeBox?.currentSession?.openingBalance ?? 0);
  const movimientoDeLaApertura = Number((aperturaQueSeGuardara - aperturaGuardada).toFixed(2));

  // Al ABRIR: la apertura que se va a guardar frente al cambio que dejó el cierre
  // anterior (el arrastre con el que se precarga el campo). Si no cuadra, el turno
  // arranca ya con una diferencia, y antes eso no se veía hasta el arqueo — donde
  // parecía un descuadre del turno.
  // Se compara la apertura FINAL y no solo el recuento: declarar 200 a mano sobre un
  // arrastre de 165 mueve el mismo dinero que contar 200.
  // Solo con arrastre > 0: una caja estrenada no tiene contra qué comparar.
  const arrastreDeLaCaja = Number(activeBox?.defaultOpeningBalance || 0);
  const descuadreAlAbrir =
    modalState.type === "open-session" && arrastreDeLaCaja > 0
      ? Number((aperturaQueSeGuardara - arrastreDeLaCaja).toFixed(2))
      : 0;
  // Mover el dinero de apertura se explica, se cuente o se teclee, y tanto al abrir como
  // al corregir una caja ya abierta — que es el gesto que reportó CRUO. Mismo criterio
  // que el ajuste a mano del esperado (ficha saldo-pagos-caja, 08-09-2026).
  const movimientoSinExplicar =
    modalState.type === "edit-open-session" ? movimientoDeLaApertura : descuadreAlAbrir;
  const faltaExplicarElDescuadre = movimientoSinExplicar !== 0 && !sessionForm.notes?.trim();
  // Sin desglose, la apertura es lo que ponga el campo — y desde que se puede vaciar,
  // un campo en blanco entraría como 0 € sin que nadie lo haya escrito. Una caja que
  // abre a cero se dice con un 0, no dejándolo vacío.
  const faltaLaApertura =
    (modalState.type === "open-session" || modalState.type === "edit-open-session") &&
    !hayDesglose(sessionForm.counts) &&
    !String(sessionForm.openingBalance ?? "").trim();

  const summary = useMemo(() => {
    const openCount = boxes.filter((box) => box.currentSession?.status === "open").length;
    const totalDifference = history.reduce((sum, item) => sum + Number(item.difference || 0), 0);
    return {
      totalBoxes: boxes.length,
      openCount,
      closedSessions: history.length,
      totalDifference,
    };
  }, [boxes, history]);

  function resetModal() {
    setModalState({ type: null, boxId: null, sessionId: null });
    setBoxForm(defaultBoxForm());
    setSessionForm(defaultSessionForm());
    // Un error viejo no puede seguir vivo en el modal siguiente: dentro del formulario
    // se lee como «lo que acabas de guardar ha fallado».
    setErrorMessage("");
  }

  function closeSessionModal({ preserveDraft = false } = {}) {
    if (
      preserveDraft &&
      modalState.boxId &&
      (
        modalState.type === "open-session" ||
        modalState.type === "close-session" ||
        modalState.type === "edit-open-session" ||
        modalState.type === "edit-closed-session"
      )
    ) {
      const key = sessionDraftKey(modalState.boxId, modalState.type);
      setSessionDrafts((prev) => ({
        ...prev,
        [key]: {
          ...sessionForm,
          counts: normalizeCounts(sessionForm.counts),
          changeAmount: sessionForm.changeAmount ?? "",
        },
      }));
    }

    setModalState({ type: null, boxId: null, sessionId: null });
    setBoxForm(defaultBoxForm());
    setSessionForm(defaultSessionForm());
    // Igual que en `resetModal`: el rechazo de antes no es el del formulario siguiente.
    // Aquí se conserva el BORRADOR, no el error.
    setErrorMessage("");
  }

  function openCreateBoxModal() {
    setBoxForm(defaultBoxForm());
    setModalState({ type: "create-box", boxId: null });
  }

  function openEditBoxModal(box) {
    setBoxForm({
      name: box.name || "",
      zone: box.zone || "Todas",
      collaborators: Array.isArray(box.collaboratorIds) ? box.collaboratorIds : [],
    });
    setModalState({ type: "edit-box", boxId: box.id });
  }

  function openSessionModal(box, mode) {
    const draft = sessionDrafts[sessionDraftKey(box.id, mode)];
    if (draft) {
      setSessionForm({
        openingBalance: draft.openingBalance ?? "",
        expectedBalance: draft.expectedBalance ?? "",
        notes: draft.notes ?? "",
        counts: normalizeCounts(draft.counts),
        changeAmount: draft.changeAmount ?? "",
        // Si al cancelar la apertura estaba escrita a mano, al volver sigue estándolo:
        // sin esto el campo volvía a seguir al recuento y se comía la cifra tecleada.
        aperturaAMano: Boolean(draft.aperturaAMano),
      });
    } else {
      const baseCounts = normalizeCounts(
        mode === "close-session" ? box.currentSession?.openingCounts : createEmptyCounts()
      );
      setSessionForm({
        openingBalance:
          mode === "open-session"
            ? String(box.defaultOpeningBalance || calculateCountsTotal(baseCounts) || "")
            : "",
        expectedBalance:
          mode === "close-session"
            ? String(box.currentSession?.expectedBalance ?? box.currentSession?.openingBalance ?? calculateCountsTotal(baseCounts))
            : "",
        notes: "",
        counts: baseCounts,
        // Cambio a dejar: por defecto el objetivo orientativo (135) al cerrar.
        changeAmount: mode === "close-session" ? String(idealChange) : "",
      });
    }
    setModalState({ type: mode, boxId: box.id, sessionId: null });
  }

  function openEditHistorySessionModal(item) {
    const draft = sessionDrafts[sessionDraftKey(item.boxId, "edit-closed-session")];
    if (draft) {
      setSessionForm({
        openingBalance: draft.openingBalance ?? "",
        expectedBalance: draft.expectedBalance ?? "",
        notes: draft.notes ?? "",
        counts: normalizeCounts(draft.counts),
        changeAmount: draft.changeAmount ?? "",
        // Si al cancelar la apertura estaba escrita a mano, al volver sigue estándolo:
        // sin esto el campo volvía a seguir al recuento y se comía la cifra tecleada.
        aperturaAMano: Boolean(draft.aperturaAMano),
      });
    } else {
      setSessionForm({
        openingBalance: "",
        expectedBalance: String(item.expectedBalance ?? ""),
        notes: item.closingNotes || item.notes || "",
        counts: normalizeCounts(item.closingCounts),
        changeAmount: item.changeTotal != null ? String(item.changeTotal) : "",
      });
    }
    setModalState({ type: "edit-closed-session", boxId: item.boxId, sessionId: item.id });
  }

  function openEditOpenSessionModal(box) {
    const draft = sessionDrafts[sessionDraftKey(box.id, "edit-open-session")];
    if (draft) {
      setSessionForm({
        openingBalance: draft.openingBalance ?? "",
        expectedBalance: draft.expectedBalance ?? "",
        notes: draft.notes ?? "",
        counts: normalizeCounts(draft.counts),
        changeAmount: draft.changeAmount ?? "",
        // Si al cancelar la apertura estaba escrita a mano, al volver sigue estándolo:
        // sin esto el campo volvía a seguir al recuento y se comía la cifra tecleada.
        aperturaAMano: Boolean(draft.aperturaAMano),
      });
    } else {
      setSessionForm({
        openingBalance: String(box.currentSession?.openingBalance ?? ""),
        expectedBalance: String(box.currentSession?.expectedBalance ?? box.currentSession?.openingBalance ?? ""),
        notes: box.currentSession?.notes || "",
        counts: normalizeCounts(box.currentSession?.openingCounts),
        changeAmount: "",
      });
    }
    setModalState({ type: "edit-open-session", boxId: box.id, sessionId: box.currentSession?.id || null });
  }

  async function upsertBox(event) {
    event.preventDefault();
    if (!token || !venueID) return;
    if (!boxForm.name.trim()) {
      setErrorMessage("El nombre de la caja es obligatorio.");
      return;
    }

    setSaving(true);
    setErrorMessage("");
    try {
      const method = modalState.type === "create-box" ? "POST" : "PATCH";
      const path =
        modalState.type === "create-box" ? "/v1/cajas" : `/v1/cajas/${activeBox?.id}`;
      const body =
        modalState.type === "create-box"
          ? {
              venue_id: venueID,
              name: boxForm.name.trim(),
              zone: boxForm.zone.trim() || "Todas",
              collaborator_user_ids: [...boxForm.collaborators].sort(),
            }
          : {
              name: boxForm.name.trim(),
              zone: boxForm.zone.trim() || "Todas",
              collaborator_user_ids: [...boxForm.collaborators].sort(),
            };

      const response = await apiFetch(path, {
        method,
        headers: apiHeaders,
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.detail || "No se pudo guardar la caja.");
      }

      await refreshBoxes();
      resetModal();
    } catch (error) {
      setErrorMessage(error.message || "No se pudo guardar la caja.");
    } finally {
      setSaving(false);
    }
  }

  async function deleteBox() {
    if (!activeBox || !token) return;
    setSaving(true);
    setErrorMessage("");
    try {
      const response = await apiFetch(`/v1/cajas/${activeBox.id}`, {
        method: "DELETE",
        headers: apiHeaders,
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.detail || "No se pudo eliminar la caja.");
      }
      await refreshBoxes();
      resetModal();
    } catch (error) {
      setErrorMessage(error.message || "No se pudo eliminar la caja.");
    } finally {
      setSaving(false);
    }
  }

  async function saveSession(event) {
    event.preventDefault();
    if (!activeBox || !token) return;
    if (faltaLaApertura) {
      setErrorMessage(
        "Escribe con cuánto dinero abre la caja, o cuenta las monedas y billetes. Si abre vacía, pon un 0."
      );
      return;
    }
    if (faltaExplicarElDescuadre) {
      setErrorMessage(
        modalState.type === "edit-open-session"
          ? "Estás moviendo la apertura de una caja abierta: escribe en las notas de dónde sale la diferencia."
          : "La apertura no cuadra con el cambio que dejó el cierre anterior: escribe en las notas de dónde sale la diferencia."
      );
      return;
    }

    setSaving(true);
    setErrorMessage("");
    try {
      const isOpenAction = modalState.type === "open-session";
      const isEditOpenSession = modalState.type === "edit-open-session";
      const isEditClosedSession = modalState.type === "edit-closed-session";
      const endpoint = isOpenAction
        ? `/v1/cajas/${activeBox.id}/open`
        : isEditOpenSession
          ? `/v1/cajas/${activeBox.id}/open-session`
        : isEditClosedSession
          ? `/v1/cajas/${activeBox.id}/sessions/${modalState.sessionId}`
          : `/v1/cajas/${activeBox.id}/close`;
      // La apertura la decide el RECUENTO cuando hay desglose, y el importe declarado
      // cuando no lo hay (decidido 23-09-2026; el backend aplica la misma regla, así
      // que una tablet con el bundle viejo tampoco puede descuadrarla). Era el segundo
      // camino a los +35 € de CRUO: apuntar 35 € más en las monedas sin tocar el campo
      // del importe dejaba `opening_balance` en 165 y el esperado sin moverse, mientras
      // la tarjeta ya cantaba 200.
      const body = isOpenAction
        ? {
            opening_balance: aperturaQueSeGuardara,
            // Sin `expected_balance`: el esperado de un turno recién abierto ES su
            // apertura, y el backend lo ignora desde el 23-09-2026.
            opening_counts: recuentoQueSeGuardara,
            notes: sessionForm.notes?.trim() || "",
          }
        : isEditOpenSession
          ? {
              // Sin `expected_balance`: el esperado de una caja abierta lo mueve el
              // backend (apertura nueva − apertura anterior, más el efectivo del
              // turno). Mandarlo era mandar la foto de cuando se abrió el modal: los
              // 35 € de una apertura corregida no entraban nunca y una venta cobrada
              // con el modal abierto se perdía (CRUO, 21-09-2026).
              opening_balance: aperturaQueSeGuardara,
              // La apertura que esta tablet tenía delante. Si otra la corrigió mientras
              // el modal (o su borrador) estaba abierto, el backend responde 409 en vez
              // de deshacer esa corrección con el valor viejo.
              opening_balance_was: Number(activeBox.currentSession?.openingBalance ?? 0),
              opening_counts: recuentoQueSeGuardara,
              notes: sessionForm.notes?.trim() || "",
            }
        : {
            expected_balance: Number(
              sessionForm.expectedBalance ||
                activeHistorySession?.expectedBalance ||
                activeBox.currentSession?.expectedBalance ||
                activeBox.currentSession?.openingBalance ||
                0
            ),
            closing_counts: mapCountsToApiEntries(sessionForm.counts),
            change_total: Number(sessionForm.changeAmount) || 0,
            notes: sessionForm.notes?.trim() || "",
          };

      const response = await apiFetch(endpoint, {
        method: isEditOpenSession || isEditClosedSession ? "PATCH" : "POST",
        headers: apiHeaders,
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.detail || "No se pudo guardar la sesión de caja.");
      }

      setSessionDrafts((prev) => {
        const next = { ...prev };
        delete next[sessionDraftKey(activeBox.id, modalState.type)];
        return next;
      });
      await refreshBoxes();
      resetModal();
    } catch (error) {
      setErrorMessage(error.message || "No se pudo guardar la sesión de caja.");
    } finally {
      setSaving(false);
    }
  }

  function toggleCollaborator(collaboratorId) {
    setBoxForm((prev) => {
      const exists = prev.collaborators.includes(collaboratorId);
      return {
        ...prev,
        collaborators: exists
          ? prev.collaborators.filter((item) => item !== collaboratorId)
          : [...prev.collaborators, collaboratorId],
      };
    });
  }

  function updateCount(field, value) {
    setSessionForm((prev) => ({
      ...prev,
      // Volver a contar devuelve el mando al recuento.
      aperturaAMano: false,
      counts: {
        ...prev.counts,
        [field]: Math.max(0, Number(value || 0)),
      },
    }));
  }


  const headerButtons = [
    { label: "Nueva caja", color: "green", onClick: openCreateBoxModal },
  ];

  return (
    <div className="space-y-4">
      <ButtonBar title="Gestión de cajas" buttons={headerButtons} />

      {errorMessage ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {errorMessage}
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-4">
        <SummaryCard label="Cajas activas" value={summary.openCount} />
        <SummaryCard label="Total cajas" value={summary.totalBoxes} />
        <SummaryCard label="Cierres registrados" value={summary.closedSessions} />
        <SummaryCard
          label="Diferencia acumulada"
          value={formatMoney(summary.totalDifference)}
          alerta={summary.totalDifference < 0}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.2fr_1fr]">
        <section className="rounded-md border border-[var(--border)] bg-[var(--card)] p-4">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Cajas</h3>
              <p className="text-sm text-gray-500">
                Crea cajas, asígnalas a colaboradores y abre o cierra turno con recuento.
              </p>
              {!canEditCajaMoney ? (
                <p className="mt-2 text-sm text-amber-700">
                  Puedes corregir el recuento de una caja <strong>abierta</strong>. Los cierres ya
                  registrados solo los puede editar el owner.
                </p>
              ) : null}
            </div>
          </div>

          <div className="space-y-3">
            {loading ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-6 py-10 text-center text-sm text-gray-600">
                Cargando cajas...
              </div>
            ) : boxes.length === 0 ? (
              <EmptyState
                title="Todavía no hay cajas"
                description="Empieza creando una caja para esta tienda. Después podrás abrirla con saldo inicial y registrar el recuento de cierre."
              />
            ) : (
              boxes.map((box) => {
                const isOpen = box.currentSession?.status === "open";
                const session = box.currentSession;
                return (
                  <div key={box.id} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-lg font-semibold text-gray-900">{box.name}</div>
                          <StatusBadge open={isOpen} />
                          <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-gray-600">
                            Zona: {box.zone || "Todas"}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          Colaboradores:{" "}
                          {box.collaborators?.length ? box.collaborators.join(", ") : "Sin asignar"}
                        </div>
                        {isOpen ? (
                          <>
                          {/* Dos por fila hasta lg: son cuatro métricas y esta columna
                              comparte fila con la botonera a partir de lg (línea ~770).
                              Con `sm:grid-cols-3` la cuarta caía sola ocupando un tercio. */}
                          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                            <MiniMetric
                              label="Abierta"
                              value={new Date(session.openedAt).toLocaleString("es-ES")}
                            />
                            <MiniMetric
                              label="Saldo apertura"
                              value={formatMoney(openingAmount(session))}
                              testId="caja-saldo-apertura"
                            />
                            {/* Regla de oro: esperado = apertura + efectivo del turno. La
                                resta es ese efectivo (ventas y aportaciones en metálico
                                menos devoluciones y retiradas), que es lo que hay que
                                cuadrar contra el TPV. Hasta el 23-09-2026 aquí salía
                                «Recuento apertura», que con desglose era el MISMO número
                                que la métrica de al lado. */}
                            <MiniMetric
                              label="Efectivo del turno"
                              value={formatMoney(session.expectedBalance - openingAmount(session))}
                              testId="caja-efectivo-del-turno"
                            />
                            {/* Lo que DEBERÍA haber en el cajón ahora: apertura + efectivo
                                que entró − el que salió (ficha saldo-pagos-caja
                                #regla-de-oro-caja). El backend ya lo mandaba y no se
                                pintaba: para verlo había que abrir «Cerrar caja», mirar el
                                campo —que es editable— y cancelar. */}
                            <MiniMetric
                              label="Esperado ahora"
                              value={formatMoney(session.expectedBalance)}
                              testId="caja-esperado-ahora"
                            />
                          </div>
                          {/* Turnos abiertos ANTES del 23-09-2026 pueden llevar dentro un
                              descuadre de apertura (se contó 200 y la apertura quedó en
                              165). No se corrige por detrás —es dinero—: se enseña, y el
                              modal de edición lo cuadra diciendo cuánto mueve. */}
                          {descuadreApertura(session) !== 0 ? (
                            <div
                              className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                              data-testid="caja-descuadre-apertura"
                            >
                              Descuadre de apertura:{" "}
                              <strong>
                                {descuadreApertura(session) > 0 ? "+" : "−"}
                                {formatMoney(Math.abs(descuadreApertura(session)))}
                              </strong>{" "}
                              entre el recuento ({formatMoney(session.openingCountedTotal)}) y la
                              apertura con la que cerrará ({formatMoney(openingAmount(session))}).
                              {canEditOpenCaja
                                ? " Entra en «Editar» para cuadrarla."
                                : " Que la cuadre un responsable antes del cierre."}
                            </div>
                          ) : null}
                          </>
                        ) : (
                          <div className="text-sm text-gray-500">
                            Último cierre:{" "}
                            {box.sessions?.[0]?.closedAt
                              ? new Date(box.sessions[0].closedAt).toLocaleString("es-ES")
                              : "Sin historial"}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <ActionButton
                          icon={<HiOutlinePencil size={16} />}
                          label="Editar"
                          tone="slate"
                          onClick={() => {
                            if (isOpen && canEditOpenCaja) {
                              openEditOpenSessionModal(box);
                              return;
                            }
                            openEditBoxModal(box);
                          }}
                        />
                        {isOpen ? (
                          <ActionButton
                            icon={<HiOutlineArchive size={16} />}
                            label="Cerrar caja"
                            tone="amber"
                            onClick={() => openSessionModal(box, "close-session")}
                          />
                        ) : (
                          <ActionButton
                            icon={<HiOutlinePlus size={16} />}
                            label="Abrir caja"
                            tone="emerald"
                            disabled={Boolean(cajaAbiertaDelLocal)}
                            onClick={() => openSessionModal(box, "open-session")}
                          />
                        )}
                      </div>
                      {/* El motivo, en TEXTO y no en un `title`: el botón deshabilitado
                          lleva `pointer-events-none`, así que el tooltip no salta ni con
                          ratón — y estas pantallas son tablets, donde no existe el hover.
                          Un botón gris sin explicación es peor que el 409 que evita. */}
                      {!isOpen && cajaAbiertaDelLocal ? (
                        <div
                          className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                          data-testid="caja-ya-hay-otra-abierta"
                        >
                          Ya hay una caja abierta en este local (
                          <strong>{cajaAbiertaDelLocal.name}</strong>). El efectivo del turno
                          tiene que entrar en una sola: ciérrala para poder abrir esta.
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className="rounded-md border border-[var(--border)] bg-[var(--card)] p-4">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Sugerencia operativa</h3>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              Lo más sólido es que cada caja tenga un único turno abierto a la vez y que el cierre
              guarde dos datos: <strong>saldo esperado</strong> y <strong>recuento real</strong>.
              Así la diferencia queda registrada aunque las ventas se controlen en otro módulo.
            </p>
          </div>
          <div className="space-y-3 rounded-xl bg-gray-50 p-4 text-sm text-gray-700">
            <div>1. Crear caja y asignar colaboradores responsables.</div>
            <div>2. Abrir caja con saldo inicial y recuento de monedas/billetes.</div>
            <div>3. Cerrar caja indicando saldo esperado y recuento real.</div>
            <div>4. Revisar diferencias en el histórico antes de consolidarlas en contabilidad.</div>
          </div>
        </section>
      </div>

      <section className="rounded-md border border-[var(--border)] bg-[var(--card)] p-4">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Historial</h3>
          <p className="text-sm text-gray-500">
            {venueName ? `Cajas abiertas y cierres en ${venueName}.` : "Cajas abiertas y cierres por caja."}
          </p>
        </div>

        {historyRows.length === 0 ? (
          <EmptyState
            title="Sin movimientos todavía"
            description="Cuando abras o cierres una caja aparecerá aquí el resumen del recuento y la diferencia."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <TableHead>
              <Th>Caja</Th>
              <Th>Apertura</Th>
              <Th>Saldo apertura</Th>
              <Th>Cierre</Th>
              <Th>Esperado</Th>
              <Th>Recuento</Th>
              <Th>Diferencia</Th>
              <Th>Cambio</Th>
              <Th>Retirada</Th>
              <Th>Notas</Th>
              <Th align="right">Acciones</Th>
            </TableHead>
              <tbody>
                {/* Filas sin border-t propio: Tr ya trae la línea del sistema; dos = separador doble */}
                {historyRows.map((item) => (
                  <Tr key={item.id} className={item.isOpen ? "bg-emerald-50/40" : ""}>
                    <Td>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800">{item.boxName}</span>
                        {item.isOpen ? <StatusBadge open /> : null}
                      </div>
                      <div className="text-xs text-gray-500">{item.zone}</div>
                    </Td>
                    <Td className="text-gray-600">
                      {new Date(item.openedAt).toLocaleString("es-ES")}
                    </Td>
                    <Td className="font-medium text-gray-800">
                      {formatMoney(openingAmount(item))}
                    </Td>
                    <Td className="text-gray-600">
                      {item.isOpen ? (
                        <span className="text-emerald-700">En curso</span>
                      ) : item.closedAt ? (
                        new Date(item.closedAt).toLocaleString("es-ES")
                      ) : (
                        "-"
                      )}
                    </Td>
                    <Td className="font-medium text-gray-800">
                      {formatMoney(item.expectedBalance)}
                    </Td>
                    <Td className="font-medium text-gray-800">
                      {item.isOpen ? (
                        <span className="text-gray-400">-</span>
                      ) : (
                        formatMoney(item.closingCountedTotal)
                      )}
                    </Td>
                    <Td>
                      {item.isOpen ? (
                        <span className="text-gray-400">-</span>
                      ) : (
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                            Number(item.difference || 0) >= 0
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {formatMoney(item.difference)}
                        </span>
                      )}
                    </Td>
                    <Td className="text-gray-700">
                      {item.isOpen ? <span className="text-gray-400">-</span> : formatMoney(item.changeTotal)}
                    </Td>
                    <Td className="font-medium text-gray-800">
                      {item.isOpen ? <span className="text-gray-400">-</span> : formatMoney(item.withdrawal)}
                    </Td>
                    <Td className="text-gray-600">
                      {item.closingNotes || item.notes || "-"}
                    </Td>
                    <Td align="right">
                      {item.isOpen ? (
                        canEditOpenCaja ? (
                          <button
                            type="button"
                            onClick={() => {
                              const box = boxes.find((b) => b.id === item.boxId);
                              if (box) openEditOpenSessionModal(box);
                            }}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                          >
                            <HiOutlinePencil size={15} />
                            Editar caja abierta
                          </button>
                        ) : null
                      ) : canEditCajaMoney ? (
                        <button
                          type="button"
                          onClick={() => openEditHistorySessionModal(item)}
                          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <HiOutlinePencil size={15} />
                          Editar cierre
                        </button>
                      ) : null}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {modalState.type === "create-box" || modalState.type === "edit-box" ? (
        <ModalShell
          title={modalState.type === "create-box" ? "Crear nueva caja" : `Editar caja ${activeBox?.name || ""}`}
          onClose={resetModal}
        >
          <form onSubmit={upsertBox} className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <div className="mb-1 text-sm font-medium text-gray-700">Nombre</div>
                <input
                  value={boxForm.name}
                  onChange={(event) => setBoxForm((prev) => ({ ...prev, name: event.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                  placeholder="Caja principal"
                />
              </label>
              <label className="block">
                <div className="mb-1 text-sm font-medium text-gray-700">Zona</div>
                <input
                  value={boxForm.zone}
                  onChange={(event) => setBoxForm((prev) => ({ ...prev, zone: event.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                  placeholder="Todas"
                />
              </label>
            </div>

            <div>
              <div className="mb-2 text-sm font-medium text-gray-700">Colaboradores asignados</div>
              {collaborators.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500">
                  No se han cargado colaboradores desde staff/owners. Puedes dejar la caja sin asignar por ahora.
                </div>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {collaborators.map((collaborator) => {
                    const selected = boxForm.collaborators.includes(collaborator.id);
                    return (
                      <button
                        key={collaborator.id}
                        type="button"
                        onClick={() => toggleCollaborator(collaborator.id)}
                        className={`rounded-lg border px-3 py-2 text-left text-sm transition ${
                          selected
                            ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                            : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {collaborator.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex flex-wrap justify-between gap-3">
              <div>
                {modalState.type === "edit-box" ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {activeBox?.currentSession?.status === "open" && canEditOpenCaja ? (
                      <Button
                        variant="outline"
                        onClick={() => openEditOpenSessionModal(activeBox)}
                        disabled={saving}
                      >
                        <HiOutlinePencil size={16} />
                        Editar dinero caja abierta
                      </Button>
                    ) : null}
                    {/* Borrar una caja con historial arrastra sus cierres (cascade) → se
                        bloquea para no perder el historial. Las cajas sin cierres sí. */}
                    {(activeBox?.sessions?.length || 0) > 0 ? (
                      <span className="text-xs text-gray-500">
                        Esta caja tiene historial de cierres y no se puede borrar.
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={deleteBox}
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <HiOutlineTrash size={16} />
                        Eliminar caja
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={resetModal}
                  disabled={saving}
                  className="inline-flex h-9 items-center justify-center rounded-sm border border-[var(--border)] px-4 text-sm font-medium text-[var(--foreground)] transition-colors hover:bg-[var(--surface-subtle)] disabled:pointer-events-none disabled:opacity-40"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex h-9 items-center justify-center rounded-sm bg-[var(--primary)] px-4 text-sm font-medium text-[var(--primary-foreground)] transition-opacity hover:opacity-[0.88] disabled:pointer-events-none disabled:opacity-40"
                >
                  {saving
                    ? "Guardando..."
                    : modalState.type === "create-box"
                      ? "Crear caja"
                      : "Actualizar caja"}
                </button>
              </div>
            </div>
          </form>
        </ModalShell>
      ) : null}

      {modalState.type === "open-session" || modalState.type === "close-session" || modalState.type === "edit-open-session" || modalState.type === "edit-closed-session" ? (
        <ModalShell
          title={
            modalState.type === "open-session"
              ? `Abrir caja ${activeBox?.name || ""}`
              : modalState.type === "edit-open-session"
                ? `Editar caja abierta ${activeBox?.name || ""}`
              : modalState.type === "edit-closed-session"
                ? `Editar cierre ${activeBox?.name || ""}`
                : `Cerrar caja ${activeBox?.name || ""}`
          }
          onClose={() => closeSessionModal({ preserveDraft: true })}
        >
          <form onSubmit={saveSession} className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              {modalState.type === "open-session" || modalState.type === "edit-open-session" ? (
                <div className="block">
                  {/* Los avisos van FUERA del <label>: con un label que envuelve al input,
                      su texto accesible es el del bloque entero, y cualquier aviso dentro
                      le cambiaba el nombre al campo. */}
                <label className="block">
                  <div className="mb-1 text-sm font-medium text-gray-700">
                    {modalState.type === "edit-open-session" ? "Saldo inicial" : "Saldo inicial esperado"}
                  </div>
                  {/* Mientras no se toque, este campo SIGUE al recuento: son dos formas de
                      decir el mismo número y no pueden discreparse (se contaban 200 € y el
                      campo se quedaba en 165, que era el que usaba el cierre).
                      Pero se teclea. Ponerlo de solo lectura dejó a CRUO sin poder escribir
                      la apertura en cuanto la caja se había abierto contando monedas
                      (reporte del 23-09-2026, con el arreglo ya en producción): escribirla
                      a mano es legítimo, solo que entonces manda lo escrito y el desglose
                      se descarta — y se dice. */}
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={
                      aperturaLaMandaElRecuento
                        ? totalDelRecuento.toFixed(2)
                        : sessionForm.openingBalance
                    }
                    onChange={(event) =>
                      setSessionForm((prev) => ({
                        ...prev,
                        openingBalance: event.target.value,
                        aperturaAMano: true,
                      }))
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                  />
                </label>
                  {aperturaLaMandaElRecuento ? (
                    <div className="mt-1 text-xs text-gray-500" data-testid="caja-apertura-es-el-recuento">
                      Sale del recuento de abajo: manda el dinero que hay en el cajón. Si
                      escribes aquí otra cifra, manda la tuya.
                    </div>
                  ) : null}
                  {elRecuentoSeVaADescartar ? (
                    <div
                      className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                      data-testid="caja-recuento-descartado"
                    >
                      Has escrito la apertura a mano, así que manda esa cifra y el desglose de
                      monedas y billetes <strong>no se guarda</strong>. Si prefieres que mande
                      el recuento, corrige las cantidades de abajo.
                    </div>
                  ) : null}
                  {descuadreAlAbrir !== 0 ? (
                    <div
                      className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                      data-testid="caja-descuadre-al-abrir"
                    >
                      El cierre anterior dejó{" "}
                      <strong>{formatMoney(arrastreDeLaCaja)}</strong> de cambio y vas a abrir con{" "}
                      <strong>{formatMoney(aperturaQueSeGuardara)}</strong>:{" "}
                      <strong>
                        {descuadreAlAbrir > 0 ? "+" : "−"}
                        {formatMoney(Math.abs(descuadreAlAbrir))}
                      </strong>
                      . Dilo en las notas antes de abrir.
                    </div>
                  ) : null}
                  {modalState.type === "edit-open-session" && movimientoDeLaApertura !== 0 ? (
                    <div
                      className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                      data-testid="caja-movimiento-apertura"
                    >
                      La apertura pasa de{" "}
                      <strong>{formatMoney(aperturaGuardada)}</strong> a{" "}
                      <strong>{formatMoney(aperturaQueSeGuardara)}</strong>. El esperado{" "}
                      {movimientoDeLaApertura > 0 ? "sube" : "baja"}{" "}
                      <strong>{formatMoney(Math.abs(movimientoDeLaApertura))}</strong>.
                    </div>
                  ) : null}
                </div>
              ) : (
                <label className="block">
                  <div className="mb-1 text-sm font-medium text-gray-700">Saldo esperado al cierre</div>
                  <input
                    type="number"
                    step="0.01"
                    value={sessionForm.expectedBalance}
                    onChange={(event) =>
                      setSessionForm((prev) => ({ ...prev, expectedBalance: event.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                  />
                </label>
              )}

              <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
                <div className="text-sm font-medium text-blue-800">Total del recuento</div>
                <div className="mt-1 text-2xl font-semibold text-blue-900">
                  {formatMoney(calculateCountsTotal(sessionForm.counts))}
                </div>
                <div className="mt-1 text-xs text-blue-700">
                  {elRecuentoSeVaADescartar
                    ? "No se guardará: has escrito la apertura a mano."
                    : modalState.type === "open-session"
                    ? "Este total se guarda como recuento de apertura."
                    : modalState.type === "edit-open-session"
                      ? "Edita el saldo y el recuento de la caja abierta antes del cierre."
                    : modalState.type === "edit-closed-session"
                      ? "Edita el recuento del cierre ya registrado y recalcula la diferencia."
                      : "Se comparará con el saldo esperado para calcular la diferencia."}
                </div>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <DenominationFieldset
                title="Recuento monedas"
                denominations={COIN_DENOMINATIONS}
                counts={sessionForm.counts}
                onChange={updateCount}
              />
              <DenominationFieldset
                title="Recuento billetes"
                denominations={NOTE_DENOMINATIONS}
                counts={sessionForm.counts}
                onChange={updateCount}
              />
            </div>

            {modalState.type === "close-session" || modalState.type === "edit-closed-session" ? (
              <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50/40 p-4">
                <div>
                  <div className="text-sm font-semibold text-amber-900">Cambio a dejar en la caja</div>
                  <div className="text-xs text-amber-700">
                    Objetivo orientativo: {formatMoney(idealChange)}. La retirada (sobre que va al
                    banco) = total contado − cambio que dejas. El cambio queda como saldo de apertura
                    del siguiente turno.
                  </div>
                </div>
                <label className="block max-w-xs">
                  <div className="mb-1 text-sm font-medium text-gray-700">Cambio a dejar (€)</div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={sessionForm.changeAmount ?? ""}
                    onChange={(event) => setSessionForm((prev) => ({ ...prev, changeAmount: event.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                    placeholder={String(idealChange)}
                  />
                </label>
                {(() => {
                  const total = calculateCountsTotal(sessionForm.counts);
                  const change = Math.max(0, Number(sessionForm.changeAmount) || 0);
                  const retirada = total - change;
                  return (
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className="rounded-lg border border-amber-200 bg-white px-3 py-2">
                        <div className="text-xs text-gray-500">Total contado</div>
                        <div className="text-lg font-semibold text-gray-900">{formatMoney(total)}</div>
                      </div>
                      <div className="rounded-lg border border-amber-200 bg-white px-3 py-2">
                        <div className="text-xs text-gray-500">Cambio que dejas</div>
                        <div className="text-lg font-semibold text-gray-900">{formatMoney(change)}</div>
                      </div>
                      <div className="rounded-lg border border-amber-300 bg-amber-100 px-3 py-2">
                        <div className="text-xs text-amber-800">Retirada (al banco)</div>
                        <div className={`text-lg font-semibold ${retirada < 0 ? "text-red-700" : "text-amber-900"}`}>
                          {formatMoney(retirada)}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : null}

            <label className="block">
              <div className="mb-1 text-sm font-medium text-gray-700">Notas</div>
              <textarea
                value={sessionForm.notes}
                onChange={(event) => setSessionForm((prev) => ({ ...prev, notes: event.target.value }))}
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                placeholder="Ej.: cambio inicial, incidencias, diferencias justificadas..."
              />
            </label>

            {/* El error TAMBIÉN dentro del modal, y PEGADO al botón: el banner de la
                página (línea ~800) queda debajo del overlay `fixed inset-0`, así que un
                guardado rechazado —por el servidor o por la guarda del descuadre— se
                veía como un botón muerto. Arriba del formulario tampoco valía: el cuerpo
                del modal tiene scroll propio y en tablet el botón queda a pantallas de
                distancia del aviso. */}
            {errorMessage ? (
              <div
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                data-testid="caja-error-del-modal"
                role="alert"
              >
                {errorMessage}
              </div>
            ) : null}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => closeSessionModal({ preserveDraft: true })}
                disabled={saving}
                className="inline-flex h-9 items-center justify-center rounded-sm border border-[var(--border)] px-4 text-sm font-medium text-[var(--foreground)] transition-colors hover:bg-[var(--surface-subtle)] disabled:pointer-events-none disabled:opacity-40"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                /* Cambiaba de verde a naranja según se abriera o se cerrara la caja.
                   Es la misma acción —confirmar el formulario que hay delante— y
                   el texto del botón ya dice cuál es; el color no añadía nada. */
                className="inline-flex h-9 items-center justify-center rounded-sm bg-[var(--primary)] px-4 text-sm font-medium text-[var(--primary-foreground)] transition-opacity hover:opacity-[0.88] disabled:pointer-events-none disabled:opacity-40"
              >
                {saving
                  ? "Guardando..."
                  : modalState.type === "open-session"
                    ? "Abrir caja"
                    : modalState.type === "edit-open-session"
                      ? "Actualizar caja abierta"
                    : modalState.type === "edit-closed-session"
                      ? "Actualizar cierre"
                      : "Cerrar caja"}
              </button>
            </div>
          </form>
        </ModalShell>
      ) : null}
    </div>
  );
}

/**
 * Las cuatro cifras de arriba. Iban en verde, azul, ámbar y rojo pastel, cada
 * una con su borde de color, y ese cuarteto no existe en el sistema: cuatro
 * fondos distintos para cuatro números que son lo mismo, un recuento.
 *
 * Ahora son la tarjeta del sistema, y el color se reserva para lo único que lo
 * necesita: una diferencia de caja en contra va en rojo EN LA CIFRA, no en la
 * caja. Es la misma regla que el saldo negativo de un socio — el dato se tiñe,
 * el contenedor no, porque si no la pantalla entera parece una alarma.
 */
function SummaryCard({ label, value, alerta = false }) {
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--card)] p-4">
      <div className="text-sm font-medium text-[var(--muted-foreground)]">{label}</div>
      <div
        className={`mt-1 text-2xl font-semibold tabular-nums ${
          alerta ? "text-[var(--destructive)]" : "text-[var(--foreground)]"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ open }) {
  return (
    <span
      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
        open ? "bg-emerald-100 text-emerald-800" : "bg-gray-200 text-gray-700"
      }`}
    >
      {open ? "Abierta" : "Cerrada"}
    </span>
  );
}

function MiniMetric({ label, value, testId }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2" data-testid={testId}>
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-sm font-semibold text-gray-800">{value}</div>
    </div>
  );
}

function EmptyState({ title, description }) {
  return (
    <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center">
      <div className="text-lg font-semibold text-gray-800">{title}</div>
      <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-gray-600">{description}</p>
    </div>
  );
}

/**
 * Los botones de cada tarjeta de caja. Eran una tercera mini paleta —slate,
 * esmeralda y ámbar— con la que «Cerrar caja» salía naranja y «Abrir caja»
 * verde, en un producto cuyo primario es slate.
 *
 * Abrir y cerrar son la acción de la tarjeta y van en primario; editar
 * acompaña, y va en contorno. Ninguno es `destructive`: cerrar un turno con su
 * recuento es consecuente, pero no destruye nada — el cierre queda registrado
 * en el histórico y es justo lo que esta pantalla existe para guardar.
 */
const INTENCION_CAJA = { slate: "outline", emerald: "primary", amber: "primary" };

function ActionButton({ icon, label, tone = "slate", onClick, disabled = false, title }) {
  return (
    <Button
      variant={INTENCION_CAJA[tone] || "outline"}
      size="sm"
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {icon}
      {label}
    </Button>
  );
}
